import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import DrawerShell from './DrawerShell';
import AcButton from './AcButton';

/**
 * V7-P4A shared confirmation dialog (design-language 03 P12 confirm rule +
 * pattern.confirm-destructive; registry component.confirm-dialog -- built
 * ONCE, composed ON the canonical DrawerShell dialog host so no second
 * dialog family exists). Exactly TWO actions: one confirm, one cancel.
 * Destructive confirms use the P12 DESTRUCTIVE treatment (danger text +
 * border, never a filled red button -- healthcare calm).
 *
 * TWO-ACTION CONTRACT (V7-P4A corrective, founder review on PR #263):
 * Confirm and Cancel are the ONLY visible/focusable actions -- the shell's
 * head Close button is hidden (confirmDialogDismissal). While idle, Escape
 * and the scrim dismiss (= Cancel); while BUSY, Escape/scrim/close are
 * structurally disabled -- never a visibly-interactive no-op.
 *
 * KEYBOARD + FOCUS: this dialog moves initial focus INTO the dialog on
 * mount (per confirmDialogInitialFocus -- destructive dialogs focus Cancel
 * so Enter never destroys by default), keeps Tab cycling inside the
 * dialog, and restores focus to the previously-focused element on unmount.
 *
 * PRESENTATION-ONLY: the caller owns the mutation; `busy` only disables
 * the controls and swaps the confirm label. No fetching, no business
 * logic, no authority decisions here.
 */

export interface ConfirmDialogProps {
  readonly title: string;
  readonly eyebrow?: string;
  /** Optional body content (plain question dialogs may omit it). */
  readonly children?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  /** P12 destructive treatment + safe default focus on Cancel. */
  readonly destructive?: boolean;
  /** True while the caller's mutation is in flight. */
  readonly busy?: boolean;
  /** Confirm label while busy (e.g. 'Saving...'). */
  readonly busyLabel?: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirmDisabled?: boolean;
  readonly confirmTestId?: string;
  readonly cancelTestId?: string;
  readonly testId?: string;
}

/**
 * Which control receives initial focus. Pure -- exported for tests.
 * Destructive dialogs focus Cancel (pressing Enter must never destroy by
 * default); ordinary confirms focus the confirm action.
 */
export function confirmDialogInitialFocus(
  destructive: boolean,
): 'confirm' | 'cancel' {
  return destructive ? 'cancel' : 'confirm';
}

/**
 * The shell dismissal controls this dialog passes to DrawerShell. Pure --
 * exported for deterministic tests. The head Close button is ALWAYS hidden
 * (the two-action contract); Escape + scrim dismiss only while idle --
 * a busy dialog blocks every dismissal path structurally.
 */
export function confirmDialogDismissal(busy: boolean): {
  hideCloseButton: true;
  closeOnScrim: boolean;
  closeOnEscape: boolean;
} {
  return {
    hideCloseButton: true,
    closeOnScrim: !busy,
    closeOnEscape: !busy,
  };
}

export default function ConfirmDialog({
  title,
  eyebrow,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  busyLabel,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  confirmTestId,
  cancelTestId,
  testId,
}: ConfirmDialogProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Initial focus in + focus restore out (dialogs trap and restore focus).
  useEffect(() => {
    const previous =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    const target =
      confirmDialogInitialFocus(destructive) === 'cancel'
        ? cancelRef.current
        : confirmRef.current;
    target?.focus();
    return () => {
      previous?.focus?.();
    };
    // Mount-only by design: focus moves once, never on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab stays inside the dialog (cycle across its focusable controls).
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab') return;
    const root = rootRef.current;
    if (root === null) return;
    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>('button:not([disabled])'),
    );
    if (focusable.length === 0) return;
    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;
    const active =
      typeof document !== 'undefined' ? document.activeElement : null;
    if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    }
  };

  const dismissal = confirmDialogDismissal(busy);

  return (
    <div ref={rootRef} onKeyDown={onKeyDown}>
      <DrawerShell
        eyebrow={eyebrow}
        title={title}
        onClose={onCancel}
        hideCloseButton={dismissal.hideCloseButton}
        closeOnScrim={dismissal.closeOnScrim}
        closeOnEscape={dismissal.closeOnEscape}
        testId={testId}
        footer={
          <div className="ac-confirm__actions">
            <AcButton
              ref={confirmRef}
              size="sm"
              variant={destructive ? 'danger' : 'primary'}
              disabled={busy || confirmDisabled}
              onClick={onConfirm}
              data-testid={confirmTestId}
            >
              {busy ? busyLabel ?? confirmLabel : confirmLabel}
            </AcButton>
            <AcButton
              ref={cancelRef}
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={onCancel}
              data-testid={cancelTestId}
            >
              {cancelLabel}
            </AcButton>
          </div>
        }
      >
        {children ?? null}
      </DrawerShell>
    </div>
  );
}
